import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Tabs from '@src/new-components/Tabs';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import useLocation from '@src/hooks/useLocation';
import { TAB_NAMES } from '@src/constants/creditIntro';
import { buildAIRows, buildAPIRows } from '@src/services/creditIntro';
import CostTable from '../CostTable';
import { StyledTabsWrapper, TABS_STYLE, getTabItemStyle } from '../index.style';
import {
  StyledCreditUsageGuide,
  StyledHeader,
  StyledTitle,
} from './index.style';

const CreditUsageGuide = ({
  isMobile,
  showTitle = false,
  numberOfElements,
}) => {
  const { t, i18n } = useTranslation();
  const { language } = i18n;
  const { getFeatureValue } = useFeatureFlags();
  const { user } = useSelector((state) => state.auth);
  const { ip } = useLocation();
  const [activeTab, setActiveTab] = useState(TAB_NAMES.AI);

  const flagContext = {
    userId: user?.id,
    email: user?.email,
    phoneNumber: user?.phoneNumber,
    ip,
  };

  const apiTtsSttLaunchEnabled = getFeatureValue(
    FEATURE_KEYS.LAUNCH_API_TTS_STT,
    flagContext,
  );

  const aiTranslateEnabled = getFeatureValue(
    FEATURE_KEYS.AI_TRANSLATE,
    flagContext,
  );
  const aiRewriteEnabled = getFeatureValue(
    FEATURE_KEYS.AI_REWRITE,
    flagContext,
  );
  const aiPresentationEnabled = getFeatureValue(
    FEATURE_KEYS.PRESENTATION_PROJECTS,
    flagContext,
  );
  const presentationAIModels = getFeatureValue(
    FEATURE_KEYS.PRESENTATION_AI_MODELS,
    flagContext,
  );
  const transcriptAudioEnabled = getFeatureValue(
    FEATURE_KEYS.TRANSCRIPT_AUDIO,
    flagContext,
  );
  const transcriptAudioModels = getFeatureValue(
    FEATURE_KEYS.TRANSCRIPT_AUDIO_MODELS,
    flagContext,
  );

  const ttsAsyncConfig =
    apiTtsSttLaunchEnabled && getFeatureValue(FEATURE_KEYS.TTS_ASYNC, { ip });
  const ttsSyncConfig =
    apiTtsSttLaunchEnabled && getFeatureValue(FEATURE_KEYS.TTS_SYNC, { ip });
  const ttsStreamingConfig =
    apiTtsSttLaunchEnabled &&
    getFeatureValue(FEATURE_KEYS.TTS_STREAMING, { ip });
  const sttAsyncConfig =
    apiTtsSttLaunchEnabled && getFeatureValue(FEATURE_KEYS.STT_ASYNC, { ip });
  const sttSyncConfig =
    apiTtsSttLaunchEnabled && getFeatureValue(FEATURE_KEYS.STT_SYNC, { ip });
  const sttStreamingConfig =
    apiTtsSttLaunchEnabled &&
    getFeatureValue(FEATURE_KEYS.STT_STREAMING, { ip });

  const tabs = [
    { name: TAB_NAMES.AI, label: 'newCreditIntroTabAI' },
    ...(apiTtsSttLaunchEnabled
      ? [{ name: TAB_NAMES.API, label: 'newCreditIntroTabAPI' }]
      : []),
  ];

  const apiRows = buildAPIRows({
    t,
    language,
    ttsAsyncConfig,
    ttsSyncConfig,
    ttsStreamingConfig,
    sttAsyncConfig,
    sttSyncConfig,
    sttStreamingConfig,
  });

  const aiRows = buildAIRows({
    t,
    language,
    aiTranslateEnabled,
    aiRewriteEnabled,
    aiPresentationEnabled,
    presentationAIModels,
    transcriptAudioEnabled,
    transcriptAudioModels,
  });

  const renderTabs = () => (
    <StyledTabsWrapper $isMobile={isMobile}>
      <Tabs
        tabs={tabs}
        activeTab={activeTab}
        onChange={setActiveTab}
        className="new-credit-intro-tabs"
        styleTabs={TABS_STYLE}
        styleTab={getTabItemStyle(isMobile)}
      />
    </StyledTabsWrapper>
  );

  return (
    <StyledCreditUsageGuide
      $isMobile={isMobile}
      $hasPadding={showTitle}
      $numberOfElements={numberOfElements}
    >
      {showTitle ? (
        <StyledHeader $isMobile={isMobile}>
          <StyledTitle $isMobile={isMobile}>
            {t('creditIntroHover')}
          </StyledTitle>
          {renderTabs()}
        </StyledHeader>
      ) : (
        renderTabs()
      )}

      {activeTab === TAB_NAMES.AI && (
        <CostTable
          rows={aiRows}
          isMobile={isMobile}
          headerName={t('newCreditIntroColumnFeatureName')}
        />
      )}
      {activeTab === TAB_NAMES.API && (
        <CostTable
          rows={apiRows}
          isMobile={isMobile}
          headerName={t('newCreditIntroColumnAPIs')}
        />
      )}
    </StyledCreditUsageGuide>
  );
};

export default CreditUsageGuide;
